import React, { useMemo } from "react";
import clsx from "clsx";
import WorkoutHistorySet from "../client-exercise-history/workout-history/WorkoutHistorySet";
import { ExerciseTypeExtra, ExerciseTypeSet, Units } from "../../constants";

import {
  Box,
  CardProps,
  Card,
  Typography,
  useTheme,
  Divider,
  Skeleton,
  Collapse,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { colorSystem } from "../../theme";
import WorkoutButton from "../workout/WorkoutButton";
import { unitSetLabelCoach } from "../../utils/units";
import { useCurrentUser } from "../../hooks/useCurrentUser";

import { ReactComponent as BetterIcon } from "../../icons/BetterTriangle.svg";
import { ReactComponent as WorseIcon } from "../../icons/WorseTriangle.svg";
import { ReactComponent as SessionCountIcon } from "../../icons/SessionCount.svg";
import { ReactComponent as RepsCountIcon } from "../../icons/RepsCount.svg";
import HistoryIcon from "../../icons/HistoryIcon";
import CloseIcon from "../../icons/CloseIcon";

import {
  extractExerciseResultSets,
  parseWorkoutResultValueJsonToWorkoutSection,
} from "../workout/utils";
import WorkoutResultsService from "../../services/WorkoutResultsService";
import { useQuery } from "@tanstack/react-query";
import { REACT_QUERY_NO_CACHING_OPTIONS } from "../../api/ReactQueryConfig";
import { ExerciseHistoryItem } from "../client-workouts/ExerciseHistoryItem";
import dayjs from "dayjs";

const useStyles = makeStyles(({ spacing, palette }) => ({
  root: {
    padding: spacing(3),
    marginBottom: spacing(2),
    overflow: "visible",
  },
  exerciseNameContainer: {
    marginBottom: spacing(1.5),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  exerciseName: {
    marginRight: spacing(1.5),
    marginBottom: spacing(0.5),
    fontFamily: "Montserrat, sans-serif",
    wordBreak: "break-word",
  },
  recorded: {
    color: palette.text.secondary,
    fontSize: 16,
    fontWeight: 400,
  },
  recordedWrapper: {
    marginBottom: spacing(2),
  },
  exerciseContainer: {
    borderBottomWidth: 1,
    borderBottomStyle: "solid",
    borderBottomColor: palette.quote,
    paddingBottom: spacing(3.4),
    marginBottom: spacing(2.9),
  },
  triangleCircle: {
    width: 22.6,
    height: 22.6,
    borderRadius: "50%",
    backgroundColor: colorSystem.greenOpacity,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  triangleCircleWorse: {
    backgroundColor: colorSystem.primaryOpacity,
  },
  statsBottom: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  statItem: {
    display: "flex",
    alignItems: "center",
  },
  statText: {
    fontSize: 18,
    fontFamily: "Montserrat, sans-serif",
    fontWeight: 500,
    marginLeft: spacing(1.3),
  },
  historyButton: {
    paddingRight: 0,
    display: "flex",
    alignItems: "center",
    borderRadius: spacing(0.5),
    height: spacing(4),
    "& svg": {
      width: spacing(2),
      height: spacing(2),
    },
  },
  historyButtonText: {
    fontSize: 18,
    fontWeight: 500,
    marginLeft: spacing(0.75),
  },
  historySetsWrapper: {
    marginTop: spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: spacing(1),
  },
  historyDateLabel: {
    fontSize: 14,
    color: palette.text.secondary,
    marginBottom: spacing(1),
  },
  exerciseContainerCustom: {
    display: "flex",
    alignItems: "center",
    gap: spacing(1),
  },
  summarySetRoot: {
    paddingLeft: spacing(0),
  },
  summarySupersetIndent: {
    paddingLeft: 0,
  },
}));
const CLIENT_SUMMARY_EXERCISE_HISTORY_QUERY_KEY =
  "client-summary-exercise-history";

export interface TrainingSummaryExerciseProps extends CardProps {
  exercise: any;
  clientUsername: string;
}

export function WorkoutSummaryExerciseSets(
  props: TrainingSummaryExerciseProps,
) {
  const { className, exercise, clientUsername, ...other } = props;
  const s = useStyles();
  const user = useCurrentUser();
  const theme = useTheme();

  const [bestResult, setBestResult] = React.useState(0);
  const [openHistory, setOpenHistory] = React.useState(false);

  const { result, title, assetId } = exercise;

  const { data: workoutExerciseResultsData, isLoading } = useQuery({
    queryKey: [
      CLIENT_SUMMARY_EXERCISE_HISTORY_QUERY_KEY,
      { clientUsername, exerciseId: assetId },
    ],
    queryFn: () =>
      WorkoutResultsService.getUserExerciseHistory(
        null,
        assetId,
        clientUsername,
      ),
    ...REACT_QUERY_NO_CACHING_OPTIONS,
  });

  const workoutExerciseResults = workoutExerciseResultsData?.items.map(
    ({ value, ...dto }) => ({
      ...dto,
      activity: {
        ...dto.activity,
        completedAt: dto.activity?.completedAt
          ? dto.activity.completedAt.toISOString()
          : undefined,
      },
      section: parseWorkoutResultValueJsonToWorkoutSection(value),
    }),
  );

  const resultSets = React.useMemo(() => {
    const rawResultSets = extractExerciseResultSets(
      workoutExerciseResults,
      assetId,
      user.units,
    );

    return rawResultSets.map((rs) => ({
      ...rs,
      sets: rs.sets?.map((s) => ({
        ...s,
        checked: s.checked === true || s.completedReps !== undefined,
      })),
    }));
  }, [workoutExerciseResults, assetId, user.units]);

  const allSum = React.useMemo(() => {
    const sumWeight =
      result?.reduce(
        (acc, item) =>
          acc +
          (!item.weight || item.weight === "-"
            ? 0
            : item.weight * (+item.completedReps || +item.reps || 0)),
        0,
      ) || 0;
    const sumReps =
      result?.reduce(
        (acc, item) =>
          acc +
          (!isNaN(item.completedReps)
            ? +item.completedReps
            : !isNaN(item.reps)
              ? +item.reps
              : 0),
        0,
      ) || 0;
    return { lbs: sumWeight, reps: sumReps };
  }, [result]);

  React.useEffect(() => {
    let max = 0;
    resultSets.forEach(({ sets }) => {
      if (Array.isArray(sets)) {
        const currentSum = sets.reduce(
          (acc, i) => acc + (i.checked ? Number(i.weight) || 0 : 0),
          0,
        );
        if (currentSum > max) max = currentSum;
      }
    });
    setBestResult(max);
  }, [resultSets]);

  return (
    <Card className={clsx(s.root, className)} {...other}>
      <Box className={s.exerciseContainer}>
        <Box className={s.exerciseNameContainer}>
          <Typography variant="h5" className={s.exerciseName}>
            {title}
          </Typography>
          <WorkoutButton
            icon={<HistoryIcon fill={theme.palette.primary.main} />}
            text="History"
            style={s.historyButton}
            styleText={s.historyButtonText}
            onClick={() => setOpenHistory(!openHistory)}
            closeIcon={<CloseIcon fill={theme.palette.primary.main} />}
            isOpen={openHistory}
          />
        </Box>

        <Box className={s.recordedWrapper}>
          <span className={s.recorded}>
            {exercise.time && `Recorded ${exercise.time}`}
          </span>
        </Box>

        {isLoading && openHistory ? (
          <Skeleton variant="text" animation="wave" height={40} />
        ) : (
          <Collapse in={openHistory} timeout="auto" unmountOnExit>
            {resultSets.length === 0 ? (
              <Typography sx={{ py: 2 }}>No results...</Typography>
            ) : (
              resultSets.map(({ sets, completedAt }, index) => (
                <React.Fragment key={index}>
                  <Box className={s.historySetsWrapper}>
                    <Typography className={s.historyDateLabel}>
                      {dayjs(completedAt).format("MMM DD.YY")}
                    </Typography>

                    <ExerciseHistoryItem
                      exercise={
                        {
                          ...exercise,
                          sets,
                        } as any
                      }
                      exIndex={index}
                      allExercises={[]}
                      classes={{
                        container: s.exerciseContainerCustom,
                        setRoot: s.summarySetRoot,
                        supersetIndent: s.summarySupersetIndent,
                      }}
                      hideHeader={true}
                      userUnits={user.units as Units}
                      collapseSectionNoLeftMargin
                    />
                  </Box>
                  {resultSets.length - 1 !== index && (
                    <Divider sx={{ marginBlock: 3 }} />
                  )}
                </React.Fragment>
              ))
            )}
          </Collapse>
        )}
      </Box>

      <Box className={s.statsBottom}>
        <Box className={s.statItem}>
          <div
            className={clsx(
              s.triangleCircle,
              bestResult > allSum.lbs && s.triangleCircleWorse,
            )}
          >
            {bestResult > allSum.lbs ? <WorseIcon /> : <BetterIcon />}
          </div>
          <span className={s.statText}>
            {`${Math.round(allSum.lbs)} ${unitSetLabelCoach(exercise?.units || user.units, exercise?.typeSet || ExerciseTypeSet.WEIGHT, "s")}`}
          </span>
        </Box>
        <Box className={s.statItem}>
          <RepsCountIcon />
          <span
            className={s.statText}
          >{`${Math.round(allSum.reps)} reps`}</span>
        </Box>
        <Box className={s.statItem}>
          <SessionCountIcon />
          <span
            className={s.statText}
          >{`Sets ${result ? result.length : 0}`}</span>
        </Box>
      </Box>
    </Card>
  );
}
